// Team profile card: colour, what the team does, lead(s), approx rota, member faces, visibility. Edit in place.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, Users, CalendarClock, Crown, Lock, Globe } from "lucide-react";
import api from "../../../lib/api";
import SquadAvatar from "../../../components/squad/SquadAvatar";
import { workspaceApi, teamColor, TEAM_SWATCHES } from "../_shared";

let squadCache = null;
export function useSquadFaces() {
  const [faces, setFaces] = useState(squadCache || {});
  useEffect(() => {
    if (squadCache) return;
    api.get("/squad").then(({ data }) => {
      const m = {}; (data.members || []).forEach((x) => { if (x.id) m[x.id] = x; if (x.user_id) m[x.user_id] = x; if (x.name) m[x.name] = x; });
      squadCache = m; setFaces(m);
    }).catch(() => {});
  }, []);
  return faces;
}

const Label = ({ icon: Icon, children }) => (
  <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 flex items-center gap-1"><Icon className="w-3 h-3" />{children}</div>
);

export default function TeamCard({ team, users, canEdit, canEditAll, onSaved, startEditing = false }) {
  const faces = useSquadFaces();
  const [edit, setEdit] = useState(false);
  const [d, setD] = useState({});
  const [members, setMembers] = useState([]);
  const [orig, setOrig] = useState([]);
  const [leads, setLeads] = useState([]);
  const col = teamColor(team);
  const start = () => {
    setD({ name: team.name, color: col, description: team.description || "", rota: team.rota || "", visibility: team.visibility || "public" });
    setMembers(team.member_ids || []); setOrig(team.member_ids || []); setLeads(team.manager_ids || []); setEdit(true);
  };
  useEffect(() => { if (startEditing && team && canEdit) start(); else setEdit(false); }, [team?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!team) return null;
  const save = async () => {
    try {
      if (canEditAll) {
        const was = new Set(orig);
        const add = members.filter((m) => !was.has(m)); const remove = orig.filter((m) => !members.includes(m));
        if (add.length || remove.length) await api.post(`/workspace/teams/${team.id}/members`, { add, remove });
      }
      const patch = { description: d.description, rota: d.rota };
      if (canEditAll) Object.assign(patch, { color: d.color, visibility: d.visibility, manager_ids: leads.filter((l) => members.includes(l)),
        ...(d.name.trim() && d.name !== team.name ? { name: d.name.trim() } : {}) });
      await workspaceApi.patchTeam(team.id, patch);
      toast.success("Team updated"); setEdit(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save team"); }
  };
  const leadSet = new Set(edit ? leads : team.manager_ids || []);
  const ppl = (edit ? members : team.member_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean)
    .sort((a, b) => Number(leadSet.has(b.id)) - Number(leadSet.has(a.id)));
  const toggleLead = (id) => setLeads((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  return (
    <div className="ws-card p-4 relative overflow-hidden" style={{ borderColor: `${col}55`, background: `linear-gradient(90deg, ${col}12, transparent 40%)` }} data-testid={`ws-team-card-${team.id}`}>
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: col }} />
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {edit && canEditAll
            ? <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className="ws-input max-w-sm text-[15px] font-semibold" data-testid="ws-team-edit-name" />
            : <div className="text-[16px] font-semibold text-white flex items-center gap-2" data-testid="ws-team-name"><span className="w-3 h-3 rounded-full" style={{ background: col }} />{team.name}
              {team.visibility === "private" && <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5"><Lock className="w-3 h-3" />Private</span>}</div>}
          {edit ? (
            <textarea value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} rows={2} placeholder="What does this team do?" className="ws-input mt-2 w-full" data-testid="ws-team-edit-description" />
          ) : <p className="text-[12.5px] text-slate-300 mt-1 max-w-3xl" data-testid="ws-team-description">{team.description || <span className="text-slate-500">No description yet — add what this team looks after.</span>}</p>}
        </div>
        {canEdit && !edit && <button onClick={start} className="ws-chip" data-testid="ws-team-edit"><Pencil className="w-3 h-3" />Edit team</button>}
        {edit && <>
          <button onClick={save} className="ws-chip on" data-testid="ws-team-save"><Check className="w-3 h-3" />Save</button>
          <button onClick={() => setEdit(false)} className="ws-chip" data-testid="ws-team-cancel"><X className="w-3 h-3" />Cancel</button>
        </>}
      </div>
      {edit && canEditAll && (
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <div className="flex items-center gap-1.5" data-testid="ws-team-edit-colors">
            <span className="text-[9.5px] uppercase tracking-widest text-slate-500 font-mono mr-1">Colour</span>
            {TEAM_SWATCHES.map((c) => <button key={c} onClick={() => setD({ ...d, color: c })} className={`w-5 h-5 rounded-full border-2 ${d.color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} data-testid={`ws-team-color-${c.slice(1)}`} />)}
          </div>
          <div className="ws-seg" data-testid="ws-team-edit-visibility">
            {[["public", "Public", Globe], ["private", "Private", Lock]].map(([k, l, I]) => (
              <button key={k} onClick={() => setD({ ...d, visibility: k })} className={d.visibility === k ? "on" : ""} data-testid={`ws-team-vis-${k}`}><I className="w-3 h-3 inline mr-1" />{l}</button>))}
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <div>
          <Label icon={CalendarClock}>Approx rota</Label>
          {edit ? <textarea value={d.rota} onChange={(e) => setD({ ...d, rota: e.target.value })} rows={3} placeholder="e.g. Mon–Fri 8–5 · Aiste early, Niraj late · Sat cover rotates" className="ws-input w-full" data-testid="ws-team-edit-rota" />
            : <div className="text-[12px] text-slate-300 whitespace-pre-wrap" data-testid="ws-team-rota">{team.rota || <span className="text-slate-500">Not set</span>}</div>}
        </div>
        <div>
          <Label icon={Users}>People · {ppl.length}{edit && canEditAll && <span className="normal-case tracking-normal text-slate-500 ml-1">— tap the crown to make someone lead</span>}</Label>
          <div className="flex flex-wrap gap-2" data-testid="ws-team-faces">
            {ppl.map((u) => { const f = faces[u.id] || faces[u.name] || {}; const lead = leadSet.has(u.id); return (
              <div key={u.id} className={`flex items-center gap-1.5 rounded-full border pl-0.5 pr-2 py-0.5 ${lead ? "bg-amber-500/10 border-amber-500/40" : "bg-white/[0.04] border-white/10"}`} title={u.email || u.name} data-testid={`ws-team-person-${u.id}`}>
                <SquadAvatar name={u.name} url={f.avatar_url} status={f.status} size={24} />
                <span className="text-[11.5px] text-slate-200">{(u.name || "").split(" ")[0]}</span>
                {lead && !edit && <Crown className="w-3 h-3 text-amber-300" data-testid={`ws-team-lead-${u.id}`} />}
                {edit && canEditAll && <button onClick={() => toggleLead(u.id)} title={lead ? "Remove as lead" : "Make team lead"} className={lead ? "text-amber-300" : "text-slate-600 hover:text-amber-300"} data-testid={`ws-team-toggle-lead-${u.id}`}><Crown className="w-3 h-3" /></button>}
                {edit && canEditAll && <button onClick={() => setMembers((m) => m.filter((x) => x !== u.id))} className="text-slate-500 hover:text-rose-300" data-testid={`ws-team-remove-${u.id}`}><X className="w-3 h-3" /></button>}
              </div>); })}
            {edit && canEditAll && (
              <select value="" onChange={(e) => e.target.value && setMembers((m) => [...m, e.target.value])} className="ws-input max-w-[170px] h-7 text-[11.5px]" data-testid="ws-team-add-member">
                <option value="">+ Add person</option>
                {users.filter((u) => !members.includes(u.id)).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
